import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AlertTriangle } from 'lucide-react';

import CollapsibleSectionHeader from '../../../OrderForm/components/CollapsibleSectionHeader';
import ApiService from '../../../../../services/ApiService';
import { OrderSchemaType } from '../order.schema';
import { FormGroup, Label, Select } from '../style';
import { useTaxProjection, Natureza } from '../Hooks/useTaxProjection';
import RateCell from './RateCell';
import {
  Blocked,
  ContextStrip,
  ContextValue,
  EmptyHint,
  Footer,
  FooterNote,
  FooterTotal,
  FixedRate,
  IpiCell,
  Ncm,
  OrigemIpiChip,
  Projected,
  ScenarioTag,
  SectionHeader,
  Table,
  TableWrapper,
  Td,
  Th,
} from './TaxationSection.style';

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const TRIBUTOS_PROJETADOS = [
  'aliquotaICMS',
  'aliquotaIPI',
  'aliquotaIBS',
  'aliquotaCBS',
  'aliquotaPIS',
  'aliquotaCOFINS',
] as const;

// ICMS (renderizado à parte, antes da célula de IPI), IBS e CBS o vendedor pode editar;
// PIS/COFINS e IPI não (natureza/regra por NCM, D2).
const TRIBUTOS_EDITAVEIS_APOS_IPI = [
  ['aliquotaIBS', 'aliquotaIBSResolvida'],
  ['aliquotaCBS', 'aliquotaCBSResolvida'],
] as const;

// PIS/COFINS vêm da natureza, não do cenário por UF: não têm par `*Resolvida` para comparar.
const TRIBUTOS_DA_NATUREZA = [
  ['aliquotaPIS', 'PIS'],
  ['aliquotaCOFINS', 'COFINS'],
] as const;

// Rótulo curto da origem do IPI (D4) — mostrado como chip sob a alíquota, só leitura (D2).
const ORIGEM_IPI_LABEL: Record<string, string> = {
  NATUREZA_NAO_INCIDE: 'não incide',
  PERFIL_NCM_EMISSOR: 'NCM · emissor',
  PERFIL_NCM_TENANT: 'NCM',
  SEM_REGRA: 'sem regra',
};

const formatarAliquota = (v?: number | null): string =>
  v === null || v === undefined ? '' : String(v).replace('.', ',');

const TaxationSection: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);
  const { control, setValue } = useFormContext<OrderSchemaType>();

  const taxConfigId = useWatch({ control, name: 'taxConfigId' });
  const supplierId = useWatch({ control, name: 'supplierId' });
  const items = useWatch({ control, name: 'items' }) as any[] | undefined;
  const [ncmsSemRegraIpi, setNcmsSemRegraIpi] = useState<Set<number>>(new Set());

  useEffect(() => {
    let ativo = true;
    ApiService.fiscal
      .listNcmProfiles({ semRegra: true, ...(supplierId ? { companyEmissorId: Number(supplierId) } : {}) })
      .then((res) => {
        if (ativo) setNcmsSemRegraIpi(new Set(res.data.itens.map((i) => i.ncmId)));
      })
      .catch(() => {
        if (ativo) setNcmsSemRegraIpi(new Set());
      });
    return () => {
      ativo = false;
    };
  }, [supplierId]);

  const { naturezas, emissor, ufDestino, bloqueio, valorDoItem, escolherNatureza } = useTaxProjection();

  const linhas = useMemo(
    () => (items ?? []).filter((item) => item?.ncmId),
    [items],
  );

  const naturezaEscolhida = naturezas.find((n: Natureza) => n.taxConfigId === taxConfigId);

  // Alíquota resolvida pela natureza é fixa. Só vira campo quando o cadastro não trouxe valor —
  // aí o vendedor informa e o buraco de cadastro fica visível em vez de virar zero silencioso.
  const aliquotaFixa = (campo: (typeof TRIBUTOS_DA_NATUREZA)[number][0]): number | null => {
    const bruto = naturezaEscolhida?.[campo];
    return bruto === null || bruto === undefined ? null : Number(bruto);
  };

  const projetadoDoItem = useCallback(
    (item: any): number | null => {
      const tax = item?.tax;
      const base = valorDoItem(item);
      if (!tax || base === null) return null;
      return TRIBUTOS_PROJETADOS.reduce(
        (acc, chave) => acc + (base * (tax[chave] ?? 0)) / 100,
        0,
      );
    },
    [valorDoItem],
  );

  const cargaProjetada = useMemo(
    () => (items ?? []).reduce((soma, item) => soma + (projetadoDoItem(item) ?? 0), 0),
    [items, projetadoDoItem],
  );

  const itensSemRegraIpi = useMemo(
    () =>
      (items ?? []).filter(
        (item) =>
          item?.tax?.origemIpi === 'SEM_REGRA' ||
          (item?.tax?.motivoNaoResolvido && item?.ncmId && ncmsSemRegraIpi.has(Number(item.ncmId))),
      ).length,
    [items, ncmsSemRegraIpi],
  );

  const atualizarAliquota = (
    indice: number,
    campo: (typeof TRIBUTOS_PROJETADOS)[number],
    valor: number | undefined,
  ) => {
    setValue(`items.${indice}.tax.${campo}` as any, valor, {
      shouldDirty: true,
    });
  };

  // Sem título: a seção é lida como continuação da Revisão da Venda.
  const header = (
    <SectionHeader>
      {!bloqueio && (
        <ContextStrip>
          <span>
            Emissor <ContextValue>{emissor ?? '—'}</ContextValue>
          </span>
          <span>
            Destino <ContextValue>{ufDestino ?? '—'}</ContextValue>
          </span>
        </ContextStrip>
      )}
    </SectionHeader>
  );

  return (
    <>
      <CollapsibleSectionHeader
        title={header}
        isOpen={isOpen}
        onToggle={() => setIsOpen((v) => !v)}
      />

      {!isOpen ? null : bloqueio ? (
        <Blocked>
          <AlertTriangle size={16} />
          <span>{bloqueio}</span>
        </Blocked>
      ) : (
        <>
          <FormGroup>
            <Label htmlFor="taxConfigId">Natureza da operação</Label>
            <Select
              id="taxConfigId"
              value={taxConfigId ?? ''}
              onChange={(e) =>
                escolherNatureza(e.target.value ? Number(e.target.value) : undefined)
              }
            >
              <option value="">Selecione a natureza da operação</option>
              {naturezas.map((n: Natureza) => (
                <option key={n.taxConfigId} value={n.taxConfigId}>
                  {n.nome ?? `CFOP ${n.cfop ?? '—'}`}
                </option>
              ))}
            </Select>
          </FormGroup>

          {naturezaEscolhida && naturezaEscolhida.ncmsDescobertos.length > 0 && (
            <Blocked>
              <AlertTriangle size={16} />
              <span>
                {naturezaEscolhida.ncmsDescobertos.length === 1
                  ? '1 item do pedido não tem cenário nesta natureza'
                  : `${naturezaEscolhida.ncmsDescobertos.length} itens do pedido não têm cenário nesta natureza`}
                . As alíquotas ficam abertas para preenchimento manual.
              </span>
            </Blocked>
          )}

          {itensSemRegraIpi > 0 && (
            <Blocked>
              <AlertTriangle size={16} />
              <span>
                {itensSemRegraIpi === 1 ? '1 item' : `${itensSemRegraIpi} itens`} sem regra de
                IPI cadastrada para o NCM; a emissão da nota vai barrar até cadastrar em
                Regras por NCM.
              </span>
            </Blocked>
          )}

          {linhas.length === 0 ? (
            <EmptyHint>
              Nenhum item com NCM no pedido. A projeção aparece quando os itens tiverem
              classificação fiscal.
            </EmptyHint>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>NCM</Th>
                    <Th>Cenário</Th>
                    <Th $align="right">ICMS %</Th>
                    <Th $align="right">IPI %</Th>
                    <Th $align="right">IBS %</Th>
                    <Th $align="right">CBS %</Th>
                    <Th $align="right">PIS %</Th>
                    <Th $align="right">COFINS %</Th>
                    <Th $align="right">Projetado</Th>
                  </tr>
                </thead>
                <tbody>
                  {(items ?? []).map((item, indice) => {
                    if (!item?.ncmId) return null;
                    const tax = item.tax;
                    const semCenario = !tax?.taxScenarioId;
                    const projetado = projetadoDoItem(item);

                    const editadoFlag = (
                      valor: number | null | undefined,
                      original: number | null | undefined,
                    ) =>
                      valor !== undefined &&
                      valor !== null &&
                      original !== undefined &&
                      original !== null &&
                      valor !== original;

                    return (
                      <tr key={item.id ?? indice}>
                        <Td>{item.nomeCustomizado ?? item.Product?.name ?? '—'}</Td>
                        <Td>
                          <Ncm>{item.ncmCodigo || item.Ncm?.ncm || item.ncm || '—'}</Ncm>
                        </Td>
                        <Td>
                          <ScenarioTag $missing={semCenario}>
                            {semCenario ? 'sem cenário' : `${ufDestino ?? ''} · resolvido`}
                          </ScenarioTag>
                        </Td>
                        <Td $align="right">
                          <RateCell
                            value={tax?.aliquotaICMS}
                            edited={editadoFlag(tax?.aliquotaICMS, tax?.aliquotaICMSResolvida)}
                            onChange={(novoValor) => atualizarAliquota(indice, 'aliquotaICMS', novoValor)}
                          />
                        </Td>
                        <Td $align="right">
                          {/* IPI não tem override na proposta (D2) — regra por NCM manda; só leitura. */}
                          <IpiCell>
                            <FixedRate title="IPI resolvido pela regra fiscal do NCM — sem edição na proposta">
                              {formatarAliquota(tax?.aliquotaIPI)}
                            </FixedRate>
                            {tax?.origemIpi && (
                              <OrigemIpiChip $semRegra={tax.origemIpi === 'SEM_REGRA'}>
                                {ORIGEM_IPI_LABEL[tax.origemIpi] ?? tax.origemIpi}
                              </OrigemIpiChip>
                            )}
                          </IpiCell>
                        </Td>
                        {TRIBUTOS_EDITAVEIS_APOS_IPI.map(([efetivo, resolvido]) => (
                          <Td key={efetivo} $align="right">
                            <RateCell
                              value={tax?.[efetivo]}
                              edited={editadoFlag(tax?.[efetivo], tax?.[resolvido])}
                              onChange={(novoValor) => atualizarAliquota(indice, efetivo, novoValor)}
                            />
                          </Td>
                        ))}
                        {TRIBUTOS_DA_NATUREZA.map(([campo, rotulo]) => {
                          const fixa = aliquotaFixa(campo);
                          const valor = tax?.[campo];
                          return (
                            <Td key={campo} $align="right">
                              {fixa !== null ? (
                                <FixedRate title={`${rotulo} da natureza da operação`}>
                                  {formatarAliquota(fixa)}
                                </FixedRate>
                              ) : (
                                <RateCell
                                  value={valor}
                                  edited={valor !== undefined && valor !== null}
                                  onChange={(novoValor) => atualizarAliquota(indice, campo, novoValor)}
                                />
                              )}
                            </Td>
                          );
                        })}
                        <Td $align="right">
                          <Projected $muted={projetado === null}>
                            {projetado === null ? '—' : moeda.format(projetado)}
                          </Projected>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}

          <Footer>
            <FooterNote>
              Valores estimados pelo cenário fiscal. Não compõem o total do pedido.
            </FooterNote>
            <FooterTotal>
              Carga tributária projetada
              <strong>{moeda.format(cargaProjetada)}</strong>
            </FooterTotal>
          </Footer>
        </>
      )}
    </>
  );
};

export default TaxationSection;
